import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Plus,
  Sparkles,
  Trash2,
  Pencil,
} from 'lucide-react';
import {
  emptyGuideBody,
  guideBodyNoun,
  guideBodyOf,
  guideBodySize,
  GUIDE_TYPE_LABELS,
  guideTypeOf,
  isGuideBodyComplete,
  normalizeGuideBody,
  type GuideBody,
  type GuideDraft,
  type GuideInput,
  type GuideWithUrl,
} from '@dmn/shared';
import { GuideBodyEditor } from '@/components/GuideBodyEditor';
import { GuideMagicButton } from '@/components/GuideMagicButton';
import { RowAction, RowActions } from '@/components/RowActions';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { GuideAiDialog } from '@/components/GuideAiDialog';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api, ApiError } from '@/lib/api';
import { useGuideHost } from '@/lib/useGuideHost';
import { queryKeys } from '@/lib/queries';

/**
 * Sentinel for "no practice" in the picker.
 *
 * A Radix `Select` cannot hold null and treats the empty string as "nothing
 * chosen", which is a different state from a deliberately shared page. This is
 * mapped back to `locationId: null` on save and never leaves the component.
 */
const GENERIC = '__all_practices__';

export const Route = createFileRoute('/guides/')({ component: GuidesIndex });

function GuidesIndex() {
  const queryClient = useQueryClient();
  const guideHost = useGuideHost();
  const [editing, setEditing] = useState<GuideWithUrl | null>(null);
  const [creating, setCreating] = useState(false);

  const guides = useQuery({ queryKey: queryKeys.guides, queryFn: api.guides.list });

  const save = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: GuideInput }) =>
      id ? api.guides.update(id, input) : api.guides.create(input),
    onSuccess: async () => {
      setCreating(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.guides });
    },
  });

  const [aiOpen, setAiOpen] = useState(false);
  /**
   * A generated page waiting to be reviewed.
   *
   * Held here rather than saved: generation produces a proposal, and the normal
   * editor is where it becomes a guide. `draftKey` forces the editor to re-seed
   * when a second draft arrives, since the form seeds once per open.
   */
  const [draft, setDraft] = useState<{ value: GuideDraft; key: string } | null>(null);

  const aiStatus = useQuery({ queryKey: queryKeys.guideAiStatus, queryFn: api.guideAi.status });
  const locationOptions = useQuery({ queryKey: queryKeys.locations, queryFn: api.locations.list });

  const generate = useMutation({
    mutationFn: (input: { prompt: string; locationId: string | undefined }) =>
      api.guideAi.generate({ prompt: input.prompt, locationId: input.locationId }),
    onSuccess: (value) => {
      setAiOpen(false);
      // Straight into the editor with the draft loaded — the operator sees the
      // page it wrote, with the type it picked, and decides from there.
      setDraft({ value, key: crypto.randomUUID() });
      setCreating(true);
    },
  });

  const unprovisioned = (guides.data ?? []).filter((g) => !g.domainProvisioned).length;

  const search = useTableSearch(guides.data, (g) => [
    g.title,
    g.slug,
    g.id,
    g.locationName,
    g.placementId,
    g.status,
    g.url,
  ]);
  const pagination = usePagination(search.filtered);
  const removeGuide = useMutation({
    mutationFn: (id: string) => api.guides.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.guides });
    },
  });

  return (
    <>
      <PageHeader
        title="Guide pages"
        description="Practice-domain landing pages — the destination behind an SMS link"
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDraft(null);
                setCreating(true);
              }}
            >
              <Plus className="size-4" />
              New guide
            </Button>
            {/* Hidden rather than disabled where no model is configured: a button
                that cannot work is worse than one that is not offered. */}
            {aiStatus.data?.available !== false && (
              <Button size="sm" onClick={() => setAiOpen(true)}>
                <Sparkles className="size-4" />
                Write with AI
              </Button>
            )}
          </div>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search title, slug, practice or link"
          label="Search guide pages"
          noun="guide page"
        />
        {unprovisioned > 0 && (
          <p className="text-sm text-muted-foreground">
            {unprovisioned} guide{unprovisioned === 1 ? '' : 's'} on the fallback host — the
            practice has not pointed DNS at us yet, so links use{' '}
            <code className="font-mono text-xs">{guideHost ?? 'the guide host'}</code>.
          </p>
        )}

        <Card className="shadow-none">
          <CardContent className="p-0">
            {guides.isError ? (
              <ErrorState error={guides.error} onRetry={() => void guides.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[240px]">Guide</TableHead>
                    <TableHead className="min-w-[150px]">Practice</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {guides.isPending && <LoadingRows columns={6} />}
                  {pagination.pageRows.map((guide) => (
                    <TableRow key={guide.id}>
                      <TableCell>
                        <p className="font-medium">{guide.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {GUIDE_TYPE_LABELS[guideTypeOf(guide)]} ·{' '}
                          {guideBodySize(guideBodyOf(guide))}{' '}
                          {guideBodyNoun(guideBodyOf(guide), guideBodySize(guideBodyOf(guide)))}
                          {guide.cta ? ` · ${guide.cta.text}` : ''}
                        </p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{guide.locationName}</TableCell>
                      <TableCell>
                        <a
                          href={guide.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-xs hover:underline"
                        >
                          {guide.url.replace(/^https:\/\//, '')}
                          <ExternalLink className="size-3" />
                        </a>
                        {!guide.domainProvisioned && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertTriangle className="ml-1 inline size-3 text-amber-600" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              This practice has no guide domain provisioned, so the link falls back
                              to {guideHost ?? 'the guide host'}.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {guide.placementId}
                      </TableCell>
                      <TableCell>
                        <Badge variant={guide.status === 'published' ? 'default' : 'secondary'}>
                          {guide.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-px text-right">
                        <RowActions>
                          <RowAction
                            icon={Pencil}
                            label={`Edit ${guide.title}`}
                            onClick={() => setEditing(guide)}
                          />
                          <RowAction
                            icon={Trash2}
                            label={`Delete ${guide.title}`}
                            destructive
                            disabled={removeGuide.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete ${guide.title}? This cannot be undone.`)) {
                                removeGuide.mutate(guide.id);
                              }
                            }}
                          />
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="guide pages" />
            {guides.data?.length === 0 && (
              <EmptyState
                title="No guide pages"
                description="A guide page is where an SMS link lands, and where the sponsored unit sits."
                icon={FileText}
                action={
                  <Button size="sm" onClick={() => setCreating(true)}>
                    New guide
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
        {removeGuide.isError && (
          <p className="text-sm text-destructive">
            {removeGuide.error instanceof ApiError ? removeGuide.error.message : 'Could not delete'}
          </p>
        )}
      </PageBody>

      <GuideDialog
        open={creating || editing !== null}
        guide={editing}
        draft={draft}
        pending={save.isPending}
        error={save.error}
        onSave={(input) => save.mutate({ id: editing?.id ?? null, input })}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
            setDraft(null);
            save.reset();
          }
        }}
      />

      <GuideAiDialog
        open={aiOpen}
        locations={locationOptions.data ?? []}
        pending={generate.isPending}
        error={generate.error}
        onGenerate={(input) => generate.mutate(input)}
        onOpenChange={(open) => {
          if (!open) {
            setAiOpen(false);
            generate.reset();
          }
        }}
      />
    </>
  );
}

function GuideDialog({
  open,
  guide,
  draft,
  pending,
  error,
  onSave,
  onOpenChange,
}: {
  open: boolean;
  guide: GuideWithUrl | null;
  /** A generated proposal to pre-fill a new guide with. Never used when editing. */
  draft?: { value: GuideDraft; key: string } | null;
  pending: boolean;
  error: unknown;
  onSave: (input: GuideInput) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const locations = useQuery({ queryKey: queryKeys.locations, queryFn: api.locations.list });
  const placements = useQuery({ queryKey: queryKeys.placements, queryFn: api.placements.list });

  const [locationId, setLocationId] = useState('');
  const [slug, setSlug] = useState('welcome-back');
  const [eyebrow, setEyebrow] = useState('Welcome back');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [body, setBody] = useState<GuideBody>(() => emptyGuideBody('checklist'));
  const [accentColor, setAccentColor] = useState<string>('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [placementId, setPlacementId] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  // Seed during render rather than in an effect — same pattern as the other
  // dialogs; an effect would paint the previous guide for a frame.
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  // A new draft is a distinct form to seed, so it joins the key — otherwise
  // generating a second page while the editor is open would show the first.
  const formKey = guide?.id ?? (draft ? `draft:${draft.key}` : 'new');
  if (open && openedFor !== formKey) {
    setOpenedFor(formKey);
    const generated = guide ? null : (draft?.value ?? null);

    // A generic guide is stored as `locationId: null`; the picker needs a value,
    // so null becomes the sentinel. Absent (a new form) stays empty, which is
    // what keeps "no practice chosen yet" distinct from "deliberately shared".
    setLocationId(guide ? (guide.locationId ?? GENERIC) : '');
    setSlug(guide?.slug ?? generated?.slug ?? 'welcome-back');
    setEyebrow(guide?.eyebrow ?? generated?.eyebrow ?? 'Welcome back');
    setTitle(guide?.title ?? generated?.title ?? '');
    setIntro(guide?.intro ?? generated?.intro ?? '');
    // The body carries its own type, so the type picker follows whatever the
    // model chose without needing to be told separately.
    setBody(guide ? guideBodyOf(guide) : (generated?.body ?? emptyGuideBody('checklist')));
    setAccentColor(guide?.accentColor ?? generated?.accentColor ?? '');
    setCtaText(guide?.cta?.text ?? generated?.cta?.text ?? '');
    setCtaUrl(guide?.cta?.url ?? generated?.cta?.url ?? '');
    setPlacementId(guide?.placementId ?? 'guide.inline');
    // Generated pages are always drafts. A person decides that patients see this.
    setStatus(guide?.status ?? 'draft');
  }
  if (!open && openedFor !== null) setOpenedFor(null);

  const webPlacements = (placements.data ?? []).filter(
    (placement) => placement.channel === 'web' && placement.status === 'active'
  );

  // Empty means "use the template default", which is why it is not simply
  // required — most practices will never set one.
  const accentValid = accentColor === '' || /^#[0-9a-fA-F]{6}$/.test(accentColor);

  const generic = locationId === GENERIC;

  // A generic page cannot name a destination — it inherits each practice's
  // booking URL — so an empty CTA destination is valid there. It is also valid on
  // a practice page, meaning "wherever this practice sends people".
  const ctaUrlValid = ctaUrl.trim() === '' || /^https?:\/\//.test(ctaUrl);

  // A guide need not ask for anything, so an empty CTA text is valid — it means
  // there is no call to action. A destination without a label is not, though:
  // that is a half-filled form rather than a deliberate omission.
  const ctaComplete = ctaText.trim() !== '' || ctaUrl.trim() === '';

  const valid =
    locationId &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) &&
    title.trim() &&
    ctaComplete &&
    ctaUrlValid &&
    placementId &&
    (generic || accentValid) &&
    isGuideBodyComplete(body);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{guide ? 'Edit guide page' : 'New guide page'}</DialogTitle>
          <DialogDescription>
            The practice&apos;s own content. The sponsored slot renders below it, and only if the
            patient has opted in.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="guide-location">Practice</Label>
              <Select value={locationId} onValueChange={setLocationId} disabled={Boolean(guide)}>
                <SelectTrigger id="guide-location">
                  <SelectValue placeholder="Select a practice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GENERIC}>All practices — shared library</SelectItem>
                  {(locations.data ?? []).map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="guide-slug">URL path</Label>
              <Input
                id="guide-slug"
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
                placeholder="welcome-back"
              />
              <p className="text-xs text-muted-foreground">
                Appears as <code className="font-mono">/{slug || '…'}</code>{' '}
                {generic ? 'on every practice domain.' : 'on the practice domain.'}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="guide-eyebrow">Eyebrow</Label>
              <Input
                id="guide-eyebrow"
                value={eyebrow}
                onChange={(event) => setEyebrow(event.target.value)}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="guide-title">Title</Label>
              <Input
                id="guide-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Your 2-minute women's health check-in"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="guide-intro">Intro</Label>
            <Textarea
              id="guide-intro"
              rows={2}
              value={intro}
              onChange={(event) => setIntro(event.target.value)}
            />
          </div>

          <GuideMagicButton
            type={body.type}
            locationId={locationId}
            ctaUrl={ctaUrl}
            disabled={pending}
            // Deliberately leaves practice, placement and status alone — the model
            // supplies words, not decisions. The slug only moves when the operator
            // has not already chosen one.
            onDraft={(draft) => {
              setTitle(draft.title);
              setEyebrow(draft.eyebrow);
              setIntro(draft.intro);
              setBody(draft.body);
              setCtaText(draft.cta.text);
              if (!ctaUrl.trim()) setCtaUrl(draft.cta.url);
              if (!guide) setSlug(draft.slug);
            }}
          />

          <GuideBodyEditor
            value={body}
            onChange={setBody}
            ownerId={guide?.id ?? 'new'}
            disabled={pending}
          />

          {generic ? (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              A shared page takes each practice&apos;s accent colour and booking URL from their
              record, so it has none of its own. Set those under Locations.
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="guide-accent">Accent colour</Label>
              <div className="flex items-center gap-2">
                <input
                  id="guide-accent"
                  type="color"
                  className="h-9 w-12 cursor-pointer rounded border bg-transparent p-1"
                  value={accentValid && accentColor ? accentColor : '#9d4265'}
                  onChange={(event) => setAccentColor(event.target.value)}
                />
                <Input
                  aria-label="Accent colour hex"
                  value={accentColor}
                  onChange={(event) => setAccentColor(event.target.value.trim())}
                  placeholder="#9d4265"
                  className="max-w-[140px] font-mono"
                />
                {accentColor && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAccentColor('')}
                  >
                    Reset
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                The practice&apos;s colour, used for the header bar, the CTA and headings. Leave
                empty for the DearDoc default. Never a sponsor&apos;s colour — this is the
                practice&apos;s page.
              </p>
              {!accentValid && (
                <p className="text-xs text-destructive">Six-digit hex, e.g. #9d4265.</p>
              )}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="guide-cta-text">CTA text (optional)</Label>
              <Input
                id="guide-cta-text"
                value={ctaText}
                onChange={(event) => setCtaText(event.target.value)}
                placeholder="Book a visit with Dr. Patel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guide-cta-url">CTA destination</Label>
              <Input
                id="guide-cta-url"
                type="url"
                value={ctaUrl}
                onChange={(event) => setCtaUrl(event.target.value)}
                placeholder={
                  generic ? "Each practice's booking URL" : 'https://practice.example/book'
                }
              />
              <p className="text-xs text-muted-foreground">
                {generic
                  ? 'Leave empty so each practice sends patients to their own booking page.'
                  : 'Leave empty to use this practice’s booking URL.'}
              </p>
              {!ctaUrlValid && (
                <p className="text-xs text-destructive">Must start with http:// or https://.</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="guide-placement">Sponsored slot</Label>
              <Select value={placementId} onValueChange={setPlacementId}>
                <SelectTrigger id="guide-placement">
                  <SelectValue placeholder="Select a placement" />
                </SelectTrigger>
                <SelectContent>
                  {webPlacements.map((placement) => (
                    <SelectItem key={placement.id} value={placement.id}>
                      {placement.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="guide-status">Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as 'draft' | 'published')}
              >
                <SelectTrigger id="guide-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft — not reachable</SelectItem>
                  <SelectItem value="published">Published — live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error != null && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof ApiError ? error.message : 'Could not save the guide'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() =>
              onSave({
                locationId: generic ? null : locationId,
                slug,
                eyebrow,
                title: title.trim(),
                intro: intro.trim(),
                body: normalizeGuideBody(body),
                // The server rejects an accent on a shared page: it would override
                // every practice's brand at once.
                accentColor: generic ? null : accentColor || null,
                cta: ctaText.trim() ? { text: ctaText.trim(), url: ctaUrl.trim() || null } : null,
                placementId,
                status,
              })
            }
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
